// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import moment from 'moment';

import Select2, { DataSelect2, EventSelect2 } from 'wdna-select2';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { waste, sensor } from '../../../requests';
import { typeSensorVariable, TypeSensorVariableListDO } from '../../../../configuration/requests';

import GraphChart from './graph/GraphChart';

interface Props {
  rowId?: number;
}

interface State {
  update: string;
  loading: boolean;

  graphInput: any;
  typeVariableArray: TypeSensorVariableListDO[];
  variables: any[];

  typeSensorId?: number;
  groupTime?: string;
  dateIni?: string;
  dateEnd?: string;
}

export default class Graph extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 1);
    const dEnd: Date = new Date();

    this.pid = window.getStr();
    this.mounted = false;

    this.state = {
      update: window.getStr(),
      loading: false,

      graphInput: {},
      typeVariableArray: [],

      variables: [],
      groupTime: 'RAW',
      dateIni: moment(dIni).format('YYYY-MM-DD'),
      dateEnd: moment(dEnd).format('YYYY-MM-DD'),
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    const { rowId } = this.props || {};

    this.loadingStart();
    try {
      const sensorInfo: any = await sensor('get', String(rowId));
      const typeSensorId: number = sensorInfo.typeSensor && sensorInfo.typeSensor.id;
      const typeVariableArray: TypeSensorVariableListDO[] = await typeSensorVariable<TypeSensorVariableListDO[]>('list', undefined, { typeSensorId }) || [];

      if (this.mounted) {
        this.setState({
          typeVariableArray,
          typeSensorId,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public submit = async (): Promise<void> => {
    const { rowId } = this.props || {};

    const req: any = {
      mode: 'CONTAINER',
      containerId: rowId,
      wheels: this.state.variables,
    };

    if (this.state.dateIni) {
      req.from = this.state.dateIni;
    }
    if (this.state.dateEnd) {
      req.to = this.state.dateEnd;
    }

    this.loadingStart();
    try {
      let data: any = [];
      // Downlaods info
      // ALL
      const data1: any = await waste('unload_graph', undefined, req);
      const gdata1: any = this.readingsLoad(data1, 'column', 0, true, 'ALL-UNLOADS');
      data = data.concat(gdata1);
      // VERIFIED
      req.verified = true;
      const data2: any = await waste('unload_graph', undefined, req);
      const gdata2: any = this.readingsLoad(data2, 'column', 0, true, 'VER-UNLOADS');
      data = data.concat(gdata2);
      // Downlaods reads
      const data3: any = await waste('unload_reads', undefined, req);
      for (let i: number = 0; i < (this.state.variables.length + 1); i++) {
        const gdata3: any = this.readingsLoad([data3[i]], 'line', 0, false);
        data = data.concat(gdata3);
      }

      this.setState({
        update: window.getStr(),
        graphInput: {
          data,
        },
      });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public toTimestamp = (strDate: string): number => {
    const datum: number = Date.parse(strDate);
    return datum / 1000;
  };

  public readingsLoad = (reqdata: any, type: any, yAxis: any, visible: boolean = true, name?: string): any => {
    const parsedData: any = reqdata.map((e: any) => {
      const serieData: any = e.data.map((point: any) => {
        const dateTimeStamp: string = point[0];
        const intervalTimeArray: [number, any] = [this.toTimestamp(dateTimeStamp) * 1000, point[1]];
        return intervalTimeArray;
      });

      return {
        type,
        yAxis,
        zIndex: 2,
        data: serieData,
        name: (name || e.name),
        visible
      };
    });

    return parsedData;
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id } = e.params.data;
    const { selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  public addValue = (id: string): void => {
    if (this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    const selection: any[] = !variables ? [] : variables.slice();
    selection.push(id);
    this.setState({
      variables: selection.sort((a: any, b: any) => {
        return a - b;
      }),
    });
  };

  public remValue = (id: string): void => {
    if (!this.isValueSelected(id)) return;
    const { variables } = this.state || {};
    if (!variables) return;
    const selection: any[] = variables.filter((itemId: any) => itemId !== id);
    this.setState({
      variables: selection,
    });
  };

  public isValueSelected = (id: string): boolean => {
    const { variables } = this.state || {};
    if (!variables) return false;
    return variables.includes(id);
  };

  public getData = (): DataSelect2[] => {
    const { typeVariableArray } = this.state;

    return typeVariableArray.filter((value: any) => {
      return value.variableName.startsWith('Wheel');
    }).map((value: any, idx: any): DataSelect2 => {
      return { id: (value.order - 1), text: `${value.variableName}` };
    });
  }

  public render(): JSX.Element {
    const { rowId } = this.props || {};
    const { typeVariableArray } = this.state;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                fullscreenbutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
              >
                <header>
                  <h2>
                    <span className="widget-icon" style={{ padding: '8px' }}>
                      <i className="fa fa-edit" />
                    </span>
                    Waste Container ID: {rowId}
                  </h2>
                  <br />
                </header>
                <br />
                <div className="container" style={{ paddingTop: '0px' }}>
                  <UiValidate>
                    <div className="row">
                      <div className="col-1">
                        <i className="fa fa-calendar">
                          <label className="label">
                            Date Ini
                          </label>
                        </i><br />
                        <input
                          type="date"
                          name="dateIni"
                          className="inputNew"
                          value={this.state.dateIni}
                          onChange={this.handleChange}
                          required
                        />
                        &nbsp;&nbsp;
                      </div>
                      <div className="col-1">
                        <i className="fa fa-calendar">
                          <label className="label">
                            Date End
                          </label>
                        </i><br />
                        <input
                          type="date"
                          name="dateEnd"
                          className="inputNew"
                          value={this.state.dateEnd}
                          onChange={this.handleChange}
                          required
                        />
                        &nbsp;&nbsp;
                      </div>
                      {this.state.groupTime === 'RAW' && (
                        <div className="col-6" id="fixed">
                          <label className="label">
                            <b>Wheels</b>
                          </label>
                          <Select2
                            multiple
                            name="variables"
                            id="variables"
                            data={this.getData()}
                            value={this.state.variables}
                            options={{
                              placeholder: 'Select variables',
                            }}
                            onSelect={this.handleChangeMultiple}
                            onUnselect={this.handleChangeMultiple}
                            style={{ width: '99%' }}
                            required
                          />
                        </div>
                      )}
                      <div className="col-3">
                        <button type="button" onClick={(): void => { this.submit().catch(() => { /* pass */ }); }} className="btn btn-default">
                          GO
                        </button>
                      </div>
                    </div>
                  </UiValidate>
                  <br /><br />
                  <GraphChart formValues={this.state} />
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
